import {
  CARD_BRANDS,
  FLOOR_LEVELS,
  MOVE_KINDS,
  MOVE_KINDS_SHORT,
  SERVICE_TYPES,
} from "@/constants";
import {
  AddressType,
  DriveTimeSegment,
  LineItem,
  Move,
  Mover,
  PaymentMethod,
} from "@/types";
import { clsx, type ClassValue } from "clsx";
import { format, parseISO, startOfDay } from "date-fns";
import { twMerge } from "tailwind-merge";

const ORDINAL_MAP = {
  1: "st",
  2: "nd",
  3: "rd",
  4: "th",
  5: "th",
  6: "th",
  7: "th",
  8: "th",
  9: "th",
  10: "th",
  11: "th",
  12: "th",
  13: "th",
  14: "th",
  15: "th",
  16: "th",
  17: "th",
  18: "th",
  19: "th",
  20: "th",
  21: "st",
  22: "nd",
  23: "rd",
  24: "th",
  25: "th",
  26: "th",
  27: "th",
  28: "th",
  29: "th",
  30: "th",
  31: "st",
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatPhoneNumberWithParens = (value: string) => {
  if (!value) return "";

  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, "");

  // Limit to 10 digits
  const trimmed = cleaned.slice(0, 10);

  // Format the number
  let formatted = trimmed;
  if (trimmed.length > 6) {
    formatted = `(${trimmed.slice(0, 3)}) ${trimmed.slice(
      3,
      6
    )}-${trimmed.slice(6)}`;
  } else if (trimmed.length > 3) {
    formatted = `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
  } else if (trimmed.length > 0) {
    formatted = `(${trimmed}`;
  }

  return formatted;
};

export const formatPhoneNumber = (value: string): string => {
  if (!value) return "";

  // Remove all non-digit characters
  const cleaned = value.replace(/\D/g, "");

  // Limit to 10 digits
  const trimmed = cleaned.slice(0, 10);

  // Format the number as NNN-NNN-NNNN
  if (trimmed.length > 6) {
    return `${trimmed.slice(0, 3)}-${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
  } else if (trimmed.length > 3) {
    return `${trimmed.slice(0, 3)}-${trimmed.slice(3)}`;
  }

  return trimmed;
};

export const createPhoneChangeHandler = (
  setPhoneNumber: (value: string) => void
) => {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setPhoneNumber(formatted);
  };
};

export function pluralize(num: number, word: string) {
  return num === 1 ? word : `${word}s`;
}

export function formatDateWithOrdinal(
  date: Date,
  {
    showDayOfWeek = false,
    showYear = true,
    showTime = false,
  }: { showDayOfWeek?: boolean; showYear?: boolean; showTime?: boolean } = {}
): string {
  const day = date.getDate();
  const suffix = ORDINAL_MAP[day as keyof typeof ORDINAL_MAP];
  return format(
    date,
    `${showDayOfWeek ? "EEE, " : ""}MMM d'${suffix}'${
      showYear ? ", yyyy" : ""
    }${showTime ? " h:mma" : ""}`
  );
}

export function formatStartWindow(
  h: number | null | undefined,
  m: number | null | undefined,
  meridiem: "AM" | "PM" | null
): string {
  if (h === null || m === null || meridiem === null) return "";
  if (h === undefined || m === undefined || meridiem === undefined) return "";

  return `${h}:${m.toString().padStart(2, "0")} ${meridiem}`;
}

// parse full time string in utc (eg 2025-01-10T18:00:00.000Z) and return formatted time
export function formatEstimatedTime(dt: string): string {
  if (!dt) return "";
  return format(parseISO(dt), "h:mma");
}

export function formatHours(hours: number | null | undefined): string {
  if (!hours) return "";

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes === 0) {
    return `${wholeHours}h`;
  } else {
    return `${wholeHours}h ${minutes}m`;
  }
}

export function humanize(str: string): string {
  const s = str.replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function getDateFromISO(date: string): Date {
  let parsedDate: Date;
  if (typeof date !== "string") {
    console.warn("Expected string date, got:", typeof date, date);
    parsedDate = new Date(date); // try to convert, or handle differently
  } else {
    parsedDate = parseISO(date);
  }

  return startOfDay(parsedDate);
}

export const formatCurrency = (
  amount: number,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(amount);
};

export const moveSalesActivities = (
  move: Move,
  { limit = 3 }: { limit?: number } = {}
) => {
  return move.sales_activities?.slice(0, limit);
};

export const formatService = (move: Move) => {
  return [
    SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] || move.service,
    move.kind && `${MOVE_KINDS[move.kind as keyof typeof MOVE_KINDS]}`,
    move.size && `${move.size}`,
    move.area && `${move.area} sq.ft.`,
  ]
    .filter(Boolean)
    .join(" / ");
};

export const formatMoveKind = (
  move: Move,
  { useShort = false }: { useShort?: boolean } = {}
) => {
  if (
    move.service === "residential" &&
    (move.kind === "apartment" || move.kind === "sfh")
  ) {
    return `${move.size} ${
      useShort
        ? MOVE_KINDS_SHORT[move.kind as keyof typeof MOVE_KINDS_SHORT]
        : MOVE_KINDS[move.kind as keyof typeof MOVE_KINDS]
    }`;
  }
  return useShort
    ? MOVE_KINDS_SHORT[move.kind as keyof typeof MOVE_KINDS_SHORT]
    : MOVE_KINDS[move.kind as keyof typeof MOVE_KINDS];
};

export const formatAddress = (
  address: AddressType,
  withAccess: boolean = false
) => {
  if (!address) return "";
  if (
    address.street === "" &&
    address.city === "" &&
    address.state === "" &&
    address.zipcode === ""
  )
    return "N/A";

  return [
    address?.street,
    address?.city,
    address?.state,
    address?.zipcode,
    withAccess && address?.floor
      ? ` (${FLOOR_LEVELS[(address?.floor || 0) as keyof typeof FLOOR_LEVELS]})`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
};

export const minutesToHours = (minutes: number) => {
  // if greater than 60, return hours and minutes else minutes
  if (minutes > 60) {
    return `${Math.floor(minutes / 60)}h ${minutes % 60}mins`;
  }
  return `${minutes.toFixed(0)}mins`;
};

export function calculateEstimatedMoveCost(
  mover: Mover,
  numMovers: number | null | undefined,
  numTrucks: number | null | undefined,
  estimatedHours: number | null | undefined,
  waypoints: DriveTimeSegment[]
) {
  if (!numMovers) return null;
  if (!numTrucks) return null;
  if (!waypoints || waypoints.length === 0) return null;
  if (!estimatedHours) return null;

  // fuel
  // default 10 miles per gallon
  // default $4.50 per gallon
  const totalDistanceMiles = waypoints.reduce(
    (acc, segment) => acc + segment.distance,
    0
  );
  const fuelCost =
    numTrucks *
    (totalDistanceMiles /
      (mover.mover_configuration.avg_miles_per_gallon || 8)) *
    (mover.mover_configuration.avg_price_per_gallon || 4.5);

  // duration is in mins
  const yardToOriginTime = waypoints[0].duration / 60;
  const destinationToYardTime = waypoints[waypoints.length - 1].duration / 60;
  const totalYardTime = yardToOriginTime + destinationToYardTime;
  const totalTime = Number(totalYardTime) + Number(estimatedHours) + 1; // add 1 hour buffer

  // crew
  // default $25/h for crew lead
  // default $20/h for helpers
  const numHelpers = numMovers - 1;
  const crewLeadCost =
    totalTime * (mover.mover_configuration.avg_hourly_pay_crew_leads || 25);
  const helpersCost =
    totalTime *
    numHelpers *
    (mover.mover_configuration.avg_hourly_pay_crew_helpers || 20);

  const crewCost = Number(crewLeadCost) + Number(helpersCost);
  const totalCost = fuelCost + crewCost;

  return {
    fuelCost,
    crewCost,
    totalCost,
  };
}

export function calculateEstimatedMoveRevenue(
  billingType: "hourly" | "flat_rate",
  estimatedHours: number | null | undefined,
  lineItems: LineItem[]
) {
  if (billingType === "hourly" && !estimatedHours) return null;
  if (!lineItems || lineItems.length === 0) return null;

  if (billingType === "hourly") {
    return (
      (lineItems[0].price || 0) * (estimatedHours || 0) +
      lineItems
        .slice(1)
        .reduce((acc, item) => acc + (item.price || 0) * (item.qty || 0), 0)
    );
  }

  return lineItems.reduce(
    (acc, item) => acc + (item.price || 0) * (item.qty || 0),
    0
  );
}

export const addressBlank = (address: AddressType) => {
  if (!address) return true;

  return (
    address.street === "" &&
    address.city === "" &&
    address.state === "" &&
    address.zipcode === ""
  );
};

export function humanizeString(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// export function displayService(move: Move) {
//   return SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] || move.service;
// }

export function showCardDetails(pm: PaymentMethod) {
  if (!pm) return null;
  if (!pm.brand) return null;
  if (!pm.last_4) return null;

  return `${CARD_BRANDS[pm.brand.toLowerCase() as keyof typeof CARD_BRANDS]} ${
    pm.last_4
  }`;
}

// Parses a time string like "08:00 AM" or "13:30" to decimal hours
export function parseTimeString(timeStr: string): number {
  if (!timeStr) return 0;
  const [time, meridiem] = timeStr.split(" ");
  const [h, m] = time.split(":").map(Number);
  let hours = h;
  const minutes = m;
  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;
  return hours + minutes / 60;
}

// Calculates start and duration percentages for timeline placement
// timelineStart and timelineEnd are the start and end hours of the timeline
// startHour and endHour are the start and end hours of the event
export function getTimelinePercentages(
  startHour: number,
  endHour: number,
  timelineStart = 6,
  timelineEnd = 18
) {
  const totalHours = timelineEnd - timelineStart;
  const startPercentage = ((startHour - timelineStart) / totalHours) * 100;
  const durationPercentage = ((endHour - startHour) / totalHours) * 100;
  return { startPercentage, durationPercentage };
}
